import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container, Typography, Box, IconButton, Card, CardContent,
  Fab, Menu, MenuItem, CircularProgress, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
} from '@mui/material';
import {
  Add, ArrowBack, ArrowForward, ArrowDownward,
  Restaurant, LocalCafe, Fastfood, LocalPizza, FlashOn, Whatshot, DeleteOutline, EditOutlined,
} from '@mui/icons-material';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale, BarElement, BarController,
  PointElement, LineElement, LineController,
} from 'chart.js';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { radius, colors, FAB_BOTTOM_OFFSET } from '../theme';
import { PageHeader, ListRow } from '../Components/ui';
import { MEAL_LABELS } from '../constants/mealLabels';

ChartJS.register(
  ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale,
  BarElement, BarController,
  PointElement, LineElement, LineController,
);

// ─── Konstanten ───────────────────────────────────────────────────────────────

const MEALS = [
  { key: 'breakfast', label: MEAL_LABELS.breakfast, icon: LocalCafe,  color: colors.meal.breakfast, path: '/breakfast' },
  { key: 'lunch',     label: MEAL_LABELS.lunch,     icon: Restaurant, color: colors.meal.lunch,     path: '/lunch'     },
  { key: 'dinner',    label: MEAL_LABELS.dinner,    icon: LocalPizza, color: colors.meal.dinner,    path: '/dinner'    },
  { key: 'snacks',    label: MEAL_LABELS.snacks,    icon: Fastfood,   color: colors.meal.snacks,    path: '/snacks'    },
];

const DAY_NAMES = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const barLabelPlugin = {
  id: 'barLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta) return;
    meta.data.forEach((bar, i) => {
      const value = chart.data.datasets[0].data[i];
      if (!value) return;
      ctx.save();
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(value, bar.x, bar.y - 3);
      ctx.restore();
    });
  },
};

// ─── Helper ───────────────────────────────────────────────────────────────────

const formatDateForDB = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateForDisplay = (date) =>
  `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;

const isToday = (date) => new Date().toDateString() === date.toDateString();

const apiPost = (url, body, token) =>
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

const Dashboard = () => {
  const navigate  = useNavigate();
  const theme     = useTheme();
  const token     = localStorage.getItem('token');
  const heroGradient = `linear-gradient(135deg, ${theme.palette.brand.gradientStart} 0%, ${theme.palette.brand.gradientEnd} 100%)`;

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const [slideDir,   setSlideDir]   = useState(null); // 'next' | 'prev' | null

  const changeDay = (direction) => {
    setSlideDir(direction);
    setCurrentDate((d) => {
      const n = new Date(d);
      n.setDate(n.getDate() + (direction === 'next' ? 1 : -1));
      return n;
    });
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0 && !isToday(currentDate)) changeDay('next');
      else if (dx > 0)                     changeDay('prev');
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const [totalCalories,      setTotalCalories]      = useState(null);
  const [consumedCalories,   setConsumedCalories]   = useState(0);
  const [caloriesByMealtime, setCaloriesByMealtime] = useState({ breakfast: 0, lunch: 0, dinner: 0, snacks: 0 });
  const [consumedFood,       setConsumedFood]       = useState([]);
  const [caloriesData,       setCaloriesData]       = useState([]);
  const [streakCount,        setStreakCount]        = useState(0);
  const [calorieStreak,      setCalorieStreak]      = useState(0);
  const [calorieStreakInfoOpen, setCalorieStreakInfoOpen] = useState(false);
  const [streakInfoOpen, setStreakInfoOpen] = useState(false);
  const [currentDate,        setCurrentDate]        = useState(new Date());
  const [isLoading,          setIsLoading]          = useState(true);
  const [anchorEl,           setAnchorEl]           = useState(null);
  const [selectedSegment,    setSelectedSegment]    = useState(null);
  const [deletingIds,        setDeletingIds]        = useState(new Set());
  const [editItem,           setEditItem]           = useState(null);
  const [editName,           setEditName]           = useState('');
  const [editKcal,           setEditKcal]           = useState('');

  // ── Daten laden ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!token) return;
    fetch('/api/users/get-settings', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d) setTotalCalories(d.calories); })
      .catch(console.error);

    fetch('/api/challenges/tracking-streak', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d && typeof d.current === 'number') setStreakCount(d.current); })
      .catch(console.error);

    fetch('/api/challenges/calorie-streak', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d && typeof d.current === 'number') setCalorieStreak(d.current); })
      .catch(console.error);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const date = formatDateForDB(currentDate);
    setIsLoading(true);

    Promise.all([
      apiPost('/api/users/get-calories',            { date }, token).then((r) => r.ok ? r.json() : null),
      apiPost('/api/users/get-calories-by-mealtime', { date }, token).then((r) => r.ok ? r.json() : null),
      apiPost('/api/users/get-consumed-calories',    { date }, token).then((r) => r.ok ? r.json() : null),
      apiPost('/api/users/get-calories-last-seven-days', { endDate: date }, token).then((r) => r.ok ? r.json() : null),
    ]).then(([kcal, byMeal, food, sevenDays]) => {
      if (kcal)      setConsumedCalories(kcal.total_calories);
      if (byMeal)    setCaloriesByMealtime(byMeal);
      if (food)      setConsumedFood(food);
      if (sevenDays) setCaloriesData(
        sevenDays
          .map((e) => ({ date: e.date, kcal: Number(e.consumedcalories) }))
          .sort((a, b) => new Date(a.date) - new Date(b.date))
      );
    }).finally(() => setIsLoading(false));
  }, [currentDate, token]);

  const isAnimEnabled = () =>
    localStorage.getItem('animationsEnabled') !== 'false' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const handleDelete = (id) => {
    if (isAnimEnabled()) {
      setDeletingIds((prev) => new Set(prev).add(id));
      setTimeout(async () => {
        try {
          const res = await fetch(`/api/users/delete-consumed-calories?id=${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            setConsumedFood((prev) => prev.filter((item) => item.id !== id));
            setDeletingIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
          }
        } catch (e) { console.error(e); }
      }, 320);
    } else {
      fetch(`/api/users/delete-consumed-calories?id=${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }).then((res) => {
        if (res.ok) setConsumedFood((prev) => prev.filter((item) => item.id !== id));
      }).catch(console.error);
    }
  };

  const handleEditOpen = (item) => {
    setEditItem(item);
    setEditName(item.name);
    setEditKcal(String(item.kcal));
  };

  const handleEditSave = async () => {
    if (!editItem) return;
    const kcalNum = Number(editKcal);
    if (!editName.trim() || !Number.isFinite(kcalNum) || kcalNum < 0) return;

    const res = await fetch('/api/users/update-consumed-calories', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ id: editItem.id, name: editName.trim(), kcal: kcalNum }),
    });

    if (res.ok) {
      const updated = await res.json();
      setConsumedFood((prev) =>
        prev.map((item) => item.id === updated.id ? { ...item, name: updated.name, kcal: updated.kcal } : item)
      );
    }
    setEditItem(null);
  };

  // ── Makro-Summen ─────────────────────────────────────────────────────────────

  const dailyMacros = useMemo(() =>
    consumedFood.reduce((acc, item) => ({
      protein: acc.protein + (Number(item.protein_g) || 0),
      fat:     acc.fat     + (Number(item.fat_g)     || 0),
      carbs:   acc.carbs   + (Number(item.carbs_g)   || 0),
    }), { protein: 0, fat: 0, carbs: 0 }),
  [consumedFood]);

  const hasMacros = dailyMacros.protein > 0 || dailyMacros.fat > 0 || dailyMacros.carbs > 0;

  // ── Donut-Chart Daten ────────────────────────────────────────────────────────

  const isOverLimit    = consumedCalories >= (totalCalories || 0);
  const remaining      = Math.max(0, (totalCalories || 0) - consumedCalories);
  const mealValues     = MEALS.map((m) => caloriesByMealtime[m.key]);
  const chartValues    = [...mealValues, remaining];
  const chartLabels    = [...MEALS.map((m) => m.label), 'Übrig'];
  const baseColors     = [...MEALS.map((m) => m.color), 'rgba(255,255,255,0.12)'];

  const donutData = useMemo(() => ({
    labels: chartLabels,
    datasets: [{
      data: chartValues,
      backgroundColor: baseColors.map((c, i) =>
        selectedSegment === null ? c : i === selectedSegment ? c : 'rgba(255,255,255,0.05)'
      ),
      borderColor: baseColors.map((c, i) => {
        if (selectedSegment !== null) return i === selectedSegment ? '#fff' : 'transparent';
        return isOverLimit ? '#FF5252' : '#69F0AE';
      }),
      borderWidth: 2,
      cutout: '75%',
      rotation: -90,
      circumference: 180,
      borderRadius: 8,
    }],
  }), [chartValues, selectedSegment, isOverLimit]);

  // Center-Text des Donut
  const centerValue = selectedSegment !== null
    ? `${totalCalories ? Math.round((chartValues[selectedSegment] / totalCalories) * 100) : 0}%`
    : isOverLimit
      ? `+${totalCalories ? Math.round(((consumedCalories - totalCalories) / totalCalories) * 100) : 0}%`
      : `${totalCalories ? Math.round((remaining / totalCalories) * 100) : 0}%`;

  const centerLabel = selectedSegment !== null
    ? chartLabels[selectedSegment]
    : isOverLimit ? 'Drüber' : 'Übrig';

  const centerSub = selectedSegment !== null
    ? `${chartValues[selectedSegment]} kcal`
    : isOverLimit ? `${consumedCalories - (totalCalories || 0)} kcal` : `${remaining} kcal`;

  // ── Balken-Chart (7 Tage) ────────────────────────────────────────────────────

  const barChartData = useMemo(() => {
    if (!caloriesData.length || totalCalories === null) return null;

    const labels = caloriesData.map(({ date }) => {
      const [y, m, d] = date.split('-').map(Number);
      return DAY_NAMES[new Date(y, m - 1, d).getDay()];
    });

    const values    = caloriesData.map((e) => e.kcal);
    const currentDateStr = formatDateForDB(currentDate);
    const barColors = values.map((v, i) => {
      const isCurrent = caloriesData[i]?.date === currentDateStr;
      if (v === 0)            return isCurrent ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)';
      if (v > totalCalories)  return isCurrent ? 'rgba(255,82,82,1)'    : 'rgba(255,82,82,0.7)';
      return isCurrent ? 'rgba(105,240,174,1)' : 'rgba(105,240,174,0.65)';
    });
    const barBorders = values.map((_, i) =>
      caloriesData[i]?.date === currentDateStr ? 'rgba(255,255,255,0.9)' : 'transparent'
    );

    return {
      labels,
      datasets: [
        {
          label: 'Kalorien',
          data: values,
          backgroundColor: barColors,
          borderColor: barBorders,
          borderWidth: 2,
          borderRadius: 6,
          borderSkipped: false,
        },
        {
          label: 'Ziel',
          data: Array(values.length).fill(totalCalories),
          type: 'line',
          borderColor: 'rgba(255,255,255,0.5)',
          borderDash: [4, 4],
          borderWidth: 1.5,
          pointRadius: 0,
          backgroundColor: 'transparent',
        },
      ],
    };
  }, [caloriesData, totalCalories]);

  // ── FAB-Menü ─────────────────────────────────────────────────────────────────

  const menuItemSx = {
    backgroundColor: 'background.paper',
    borderRadius: radius.lg,
    mx: 1, my: 0.5,
    px: 2, py: 1,
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    '&:hover': { backgroundColor: 'action.hover' },
  };

  const fabMenu = [
    ...MEALS.map((m) => ({ label: m.label, icon: m.icon, path: m.path })),
    { label: 'Schnelltracking', icon: FlashOn, path: '/quickfood' },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="sm" sx={{ pt: 2, pb: 14 }} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>

      {/* ── Header ── */}
      <PageHeader
        title="Dashboard"
        trailingAction={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {streakCount > 0 && (
              <Box
                onClick={() => setStreakInfoOpen(true)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  bgcolor: (t) => alpha(t.palette.streak.current, 0.12),
                  borderRadius: radius.pill,
                  px: 1.5, py: 0.5, cursor: 'pointer',
                }}
              >
                <Whatshot sx={{ color: 'streak.current', fontSize: 18 }} />
                <Typography variant="body2" fontWeight="bold" sx={{ color: 'streak.current' }}>
                  {streakCount}
                </Typography>
              </Box>
            )}
            {calorieStreak > 0 && (
              <Box
                onClick={() => setCalorieStreakInfoOpen(true)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  bgcolor: (t) => alpha(t.palette.streak.best, 0.12),
                  borderRadius: radius.pill,
                  px: 1.5, py: 0.5, cursor: 'pointer',
                }}
              >
                <ArrowDownward sx={{ color: 'streak.best', fontSize: 18 }} />
                <Typography variant="body2" fontWeight="bold" sx={{ color: 'streak.best' }}>
                  {calorieStreak}
                </Typography>
              </Box>
            )}
          </Box>
        }
      />

      {/* ── Datums-Navigation ── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: 'action.hover', borderRadius: radius.xl, px: 1, py: 0.5, mb: 3,
      }}>
        <IconButton onClick={() => changeDay('prev')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="body1" fontWeight="600">
          {isToday(currentDate) ? 'Heute' : formatDateForDisplay(currentDate)}
        </Typography>
        <IconButton
          onClick={() => changeDay('next')}
          disabled={isToday(currentDate)}
        >
          <ArrowForward />
        </IconButton>
      </Box>

      <Box
        key={`${formatDateForDB(currentDate)}-${slideDir}`}
        sx={isAnimEnabled() && slideDir ? {
          animation: `${slideDir === 'next' ? 'dashSlideRight' : 'dashSlideLeft'} 0.28s cubic-bezier(0.25,0.46,0.45,0.94) both`,
          '@keyframes dashSlideRight': {
            from: { opacity: 0, transform: 'translateX(36px)' },
            to:   { opacity: 1, transform: 'translateX(0)' },
          },
          '@keyframes dashSlideLeft': {
            from: { opacity: 0, transform: 'translateX(-36px)' },
            to:   { opacity: 1, transform: 'translateX(0)' },
          },
        } : {}}
      >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* ── Kalorienbudget (Hero) ── */}
          <Card sx={{ mb: 2.5, background: heroGradient, borderRadius: radius.xl }}>
            <CardContent>
              {/* Stats-Zeile */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                {[
                  { label: 'Aufgenommen', value: consumedCalories },
                  { label: 'Verbleibend', value: totalCalories !== null ? totalCalories - consumedCalories : '–' },
                ].map(({ label, value }) => (
                  <Box key={label} sx={{
                    flex: 1, textAlign: 'center',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: radius.xl, py: 1.5, backdropFilter: 'blur(4px)',
                  }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block' }}>
                      {label}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff', lineHeight: 1.2 }}>
                      {value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>kcal</Typography>
                  </Box>
                ))}
              </Box>

              {/* Donut-Chart */}
              <Box sx={{ position: 'relative', mx: 'auto', maxWidth: 280 }}>
                <Doughnut
                  data={donutData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    aspectRatio: 1.7,
                    plugins: { legend: { display: false }, tooltip: { enabled: false } },
                    onClick: (_, elements) => {
                      if (elements.length > 0) {
                        const i = elements[0].index;
                        setSelectedSegment((prev) => prev === i ? null : i);
                      } else {
                        setSelectedSegment(null);
                      }
                    },
                  }}
                />
                <Box sx={{
                  position: 'absolute', bottom: '4px', left: '50%',
                  transform: 'translateX(-50%)', textAlign: 'center', pointerEvents: 'none',
                }}>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#fff', lineHeight: 1 }}>
                    {centerValue}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {centerLabel}
                  </Typography>
                  {selectedSegment !== null && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                      {centerSub}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* ── Mahlzeiten (tappable) ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2.5 }}>
            {MEALS.map((meal) => {
              const Icon = meal.icon;
              return (
                <Box
                  key={meal.key}
                  onClick={() => navigate(`${meal.path}?date=${formatDateForDB(currentDate)}`)}
                  sx={{
                    border: 1, borderColor: 'divider', borderRadius: radius.xl,
                    p: 2, cursor: 'pointer', transition: 'all 0.15s',
                    '&:active': { transform: 'scale(0.97)' },
                    display: 'flex', flexDirection: 'column', gap: 0.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: meal.color, flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {meal.label}
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                    {caloriesByMealtime[meal.key]}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">kcal</Typography>
                </Box>
              );
            })}
          </Box>

          {/* ── Makros ── */}
          {hasMacros && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mb: 2.5 }}>
              {[
                { label: 'Protein',       value: dailyMacros.protein, color: colors.macro.protein },
                { label: 'Kohlenhydrate', value: dailyMacros.carbs,   color: colors.macro.carbs   },
                { label: 'Fett',          value: dailyMacros.fat,     color: colors.macro.fat      },
              ].map(({ label, value, color }) => (
                <Box
                  key={label}
                  sx={{
                    border: 1, borderColor: 'divider', borderRadius: radius.xl,
                    p: 1.5, textAlign: 'center',
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, mx: 'auto', mb: 0.75 }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>{value}</Typography>
                  <Typography variant="caption" color="text.secondary">g</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontSize: 10 }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* ── 7-Tage Balkendiagramm ── */}
          {barChartData && (
            <Card sx={{ mb: 2.5, borderRadius: radius.xl, background: heroGradient }}>
              <CardContent>
                <Typography variant="body2" fontWeight={700} sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                  Letzte 7 Tage
                </Typography>
                <Box sx={{ height: 180 }}>
                  <Bar
                    data={barChartData}
                    plugins={[barLabelPlugin]}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: 'rgba(0,0,0,0.75)',
                          callbacks: {
                            label: (ctx) => ctx.dataset.label === 'Ziel'
                              ? `Ziel: ${ctx.raw} kcal`
                              : `${ctx.raw} kcal`,
                          },
                        },
                      },
                      onClick: (_, elements) => {
                        if (elements.length > 0) {
                          const idx = elements[0].index;
                          const entry = caloriesData[idx];
                          if (entry) {
                            const [y, m, d] = entry.date.split('-').map(Number);
                            setCurrentDate(new Date(y, m - 1, d));
                          }
                        }
                      },
                      scales: {
                        x: {
                          ticks: { color: 'rgba(255,255,255,0.7)', font: { size: 11 } },
                          grid: { display: false },
                          border: { display: false },
                        },
                        y: {
                          display: false,
                          beginAtZero: true,
                          max: Math.ceil(
                            (Math.max(...caloriesData.map((e) => e.kcal), totalCalories || 0) + 300) / 100
                          ) * 100,
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                  {[
                    { color: 'rgba(105,240,174,0.85)', label: 'Im Ziel' },
                    { color: 'rgba(255,82,82,0.85)',   label: 'Überschritten' },
                  ].map(({ color, label }) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: color }} />
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{label}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* ── Gegessene Lebensmittel ── */}
          <Card sx={{ mb: 2, borderRadius: radius.xl }}>
            <CardContent sx={{ pb: '16px !important' }}>
                      <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                {isToday(currentDate) ? 'Heute gegessen' : `Am ${formatDateForDisplay(currentDate)} gegessen`}
              </Typography>

              {consumedFood.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  Noch nichts eingetragen.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {consumedFood.map((item, idx) => (
                    <ListRow
                      key={item.id}
                      variant="flat"
                      title={item.name}
                      subtitle={`${item.kcal} kcal`}
                      trailing={
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleEditOpen(item)}
                            sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(item.id)}
                            sx={{ color: 'text.disabled', ml: 0.5, '&:hover': { color: 'error.main' } }}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </>
                      }
                      sx={{
                        px: 0, py: 1.25,
                        borderBottom: idx === consumedFood.length - 1 ? 0 : 1,
                        overflow: 'hidden',
                        ...(deletingIds.has(item.id) && {
                          animation: 'rowSlideOut 0.3s ease forwards',
                          '@keyframes rowSlideOut': {
                            '0%':   { opacity: 1, transform: 'translateX(0)',    maxHeight: '80px', paddingTop: '10px', paddingBottom: '10px' },
                            '100%': { opacity: 0, transform: 'translateX(56px)', maxHeight: '0px',  paddingTop: '0px',  paddingBottom: '0px'  },
                          },
                        }),
                      }}
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* ── Bearbeiten-Dialog ── */}
          <Dialog open={Boolean(editItem)} onClose={() => setEditItem(null)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="h6" fontWeight={700}>Eintrag bearbeiten</Typography>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
              <TextField
                label="Name"
                fullWidth
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 200 } }}
              />
              <TextField
                label="Kalorien (kcal)"
                type="number"
                fullWidth
                value={editKcal}
                onChange={(e) => setEditKcal(e.target.value)}
                onFocus={(e) => e.target.select()}
                slotProps={{ htmlInput: { inputMode: 'numeric', min: 0 } }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
              <Button
                onClick={() => setEditItem(null)}
                variant="outlined"
                sx={{ flex: 1 }}
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleEditSave}
                variant="contained"
                disabled={!editName.trim() || Number(editKcal) < 0 || !Number.isFinite(Number(editKcal))}
                sx={{ flex: 1, fontWeight: 700 }}
              >
                Speichern
              </Button>
            </DialogActions>
          </Dialog>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            PaperProps={{
              elevation: 0,
              sx: { bgcolor: 'transparent', boxShadow: 'none' },
            }}
          >
            {fabMenu.map(({ label, icon: Icon, path }) => (
              <MenuItem
                key={label}
                onClick={() => { navigate(`${path}?date=${formatDateForDB(currentDate)}`); setAnchorEl(null); }}
                sx={menuItemSx}
              >
                <Icon sx={{ mr: 1.5, fontSize: 20 }} />
                <Typography variant="body2" fontWeight={600}>{label}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
      </Box>

      {/* ── FAB + Blur-Overlay ── */}
      {/* Außerhalb der animierten Box gerendert, da ein `transform` auf einem
          Vorfahren einen neuen containing block für `position: fixed` erzeugt
          und der Button sonst nicht mehr relativ zum Viewport positioniert wird. */}
      {!isLoading && (
        <>
          {Boolean(anchorEl) && (
            <Box
              onClick={() => setAnchorEl(null)}
              sx={{
                position: 'fixed', inset: 0,
                backdropFilter: 'blur(6px)',
                backgroundColor: 'rgba(0,0,0,0.25)',
                zIndex: 5,
              }}
            />
          )}

          <Fab
            color="primary"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ position: 'fixed', bottom: FAB_BOTTOM_OFFSET, right: 20, zIndex: 10 }}
          >
            <Add />
          </Fab>
        </>
      )}

      <Dialog open={streakInfoOpen} onClose={() => setStreakInfoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <Whatshot sx={{ color: 'streak.current' }} /> Streak
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Zeigt, an wie vielen Tagen in Folge du etwas getrackt hast –
            aktuell <strong>{streakCount} {streakCount === 1 ? 'Tag' : 'Tage'}</strong>.
            Die Serie bricht ab, sobald ein Tag ohne Eintrag vergeht.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button fullWidth variant="contained" onClick={() => setStreakInfoOpen(false)}>
            Verstanden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={calorieStreakInfoOpen} onClose={() => setCalorieStreakInfoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <ArrowDownward sx={{ color: 'streak.best' }} /> Kalorien-Streak
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Zeigt, an wie vielen Tagen in Folge du unter deinem Kalorienziel geblieben bist –
            aktuell <strong>{calorieStreak} {calorieStreak === 1 ? 'Tag' : 'Tage'}</strong>.
            Die Serie bricht ab, wenn du an einem Tag nichts trackst oder dein Ziel überschreitest.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button fullWidth variant="contained" onClick={() => setCalorieStreakInfoOpen(false)}>
            Verstanden
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Dashboard;
